import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PointsActions from '../../../../actions/PointsActions';
import ResultsStore from '../../../../stores/ResultsStore';
import { getMaxPointsForDeal } from 'arbitrip-common/client/utils/PointsUtils';
import classNames from 'clsx';
import Analytics from 'arbitrip-common/client/analytics';

import { ReactComponent as ElalMatmidLogo } from '@/img/elal/logo-elal-matmid.svg';

const SLIDER_VALUE_KEYS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown'];

const PointsSliderComponent = ({ points, profile, deal, couponValidation, isFormLocked }) => {
    const { t } = useTranslation();

    // Calculate deal limit
    const couponDiscountMajor = useMemo(() => {
        const eligible = !!couponValidation?.eligible;
        const discountAmount = couponValidation?.discount?.amount || 0;
        const discountAmountNum = Number(discountAmount);
        if (!eligible || !Number.isFinite(discountAmountNum) || (discountAmountNum <= 0)) {
            return 0;
        }
        return Math.max(0, discountAmountNum || 0);
    }, [couponValidation]);

    const originalDealTotalMajor = useMemo(() => {
        const candidates = [deal?.totalPrice, deal?.expedia_pricing?.totalPrice]
            .map((value) => Number(value))
            .filter(Number.isFinite);
        return candidates.length ? candidates[0] : 0;
    }, [deal]);

    const remainingDealTotalMajor = useMemo(() => {
        return Math.max(0, originalDealTotalMajor - couponDiscountMajor);
    }, [originalDealTotalMajor, couponDiscountMajor]);

    // Build adjusted deal reflecting coupon discount so max points is capped by remaining price
    const adjustedDeal = useMemo(() => {
        if (!deal || !couponDiscountMajor) return deal;
        const nights = deal?.details?.nights || 0;
        const d = { ...deal };
        const totalPriceNum = Number(d.totalPrice);
        if (Number.isFinite(totalPriceNum)) {
            d.totalPrice = Math.max(0, totalPriceNum - couponDiscountMajor);
        }
        const expediaTotalPriceNum = Number(d?.expedia_pricing?.totalPrice);
        if (d.expedia_pricing && Number.isFinite(expediaTotalPriceNum)) {
            d.expedia_pricing = {
                ...d.expedia_pricing,
                totalPrice: Math.max(0, expediaTotalPriceNum - couponDiscountMajor),
            };
        }
        const pricePerNightNum = Number(d.pricePerNight);
        const nightsNum = Number(nights) || 0;
        if (Number.isFinite(pricePerNightNum) && nightsNum > 0) {
            d.pricePerNight = Math.max(0, pricePerNightNum - couponDiscountMajor / nightsNum);
        }
        return d;
    }, [deal, couponDiscountMajor]);

    // Calculate deal limit using adjusted deal (after coupon)
    const dealLimit = useMemo(() => {
        if (remainingDealTotalMajor <= 0) {
            return 0;
        }
        if (!adjustedDeal || !profile || !points) {
            return points?.balance || 0;
        }
        return getMaxPointsForDeal(adjustedDeal, profile, points);
    }, [adjustedDeal, profile, points, remainingDealTotalMajor]);

    // Determine if EL AL provider
    const isElalProvider = points?.provider === 'elal' || points?.points_provider === 'elal';

    // Get user name from profile
    const userName = useMemo(() => {
        if (!profile) return '';
        const firstName = profile.first_name || '';
        const lastName = profile.last_name || '';
        return `${firstName} ${lastName}`.trim();
    }, [profile]);

    // State hooks - must be called before any conditional returns
    const [progress, setProgress] = useState(0);
    const [selectedPoints, setSelectedPoints] = useState(0);
    const [showPricesInPoints, setShowPricesInPoints] = useState(() => {
        return ResultsStore.getShowPricesInPoints();
    });
    const hasInitializedRef = useRef(false);

    // Listen to ResultsStore for showPricesInPoints changes
    useEffect(() => {
        const handleResultsChange = () => {
            setShowPricesInPoints(ResultsStore.getShowPricesInPoints());
        };

        handleResultsChange();
        ResultsStore.addChangeListener(handleResultsChange);

        return () => {
            ResultsStore.removeChangeListener(handleResultsChange);
        };
    }, []);

    // Initialize selectedPoints from Redux store if available
    // On initial load, sync with toggle state if no user_chosen_points exists
    useEffect(() => {
        if (!points || !points?.balance) return;
        const maxPoints = Math.min(points.balance, dealLimit);
        
        // If user_chosen_points exists, use that value (respect user's previous choice)
        if (points?.user_chosen_points !== undefined && points?.user_chosen_points !== null) {
            const clampedPoints = Math.min(points.user_chosen_points, maxPoints);
            setSelectedPoints(clampedPoints);
            if (points.user_chosen_points !== clampedPoints) {
                // If coupon reduced the deal limit (or balance dropped), clamp and persist
                PointsActions.saveChosenUserPoints(clampedPoints);
            }
            setProgress(maxPoints > 0 ? (clampedPoints / maxPoints) * 100 : 0);
            hasInitializedRef.current = true;
            return;
        }
        
        // Only sync with toggle on initial load (when no user_chosen_points exists)
        if (!hasInitializedRef.current) {
            let initialPoints;
            if (showPricesInPoints) {
                // Toggle ON: set to max points
                initialPoints = maxPoints;
            } else {
                // Toggle OFF: set to min (0)
                initialPoints = 0;
            }
            setSelectedPoints(initialPoints);
            PointsActions.saveChosenUserPoints(initialPoints);
            setProgress(maxPoints > 0 ? (initialPoints / maxPoints) * 100 : 0);
            hasInitializedRef.current = true;
        }
    }, [points, points?.user_chosen_points, points?.balance, dealLimit, showPricesInPoints]);

    useEffect(() => {
        if (!points || !points?.balance) return;
        const maxPoints = Math.min(points.balance, dealLimit);
        setProgress(maxPoints > 0 ? (selectedPoints / maxPoints) * 100 : 0);
    }, [selectedPoints, points, points?.balance, dealLimit]);

    // Clamp selected points proactively when coupon reduces the limit
    useEffect(() => {
        if (!points || !points?.balance) return;
        const maxPoints = Math.min(points.balance, dealLimit);
        if (selectedPoints > maxPoints) {
            const clamped = maxPoints;
            setSelectedPoints(clamped);
            PointsActions.saveChosenUserPoints(clamped);
            setProgress(maxPoints > 0 ? (clamped / maxPoints) * 100 : 0);
        }
    }, [couponDiscountMajor, dealLimit, points, points?.balance, selectedPoints]);

    const handleChange = (e) => {
        if (!points || dealLimit <= 0) return;
        // Remove all non-digit characters and parse the value
        const rawValue = e.target.value.replace(/\D/g, '');
        const value = rawValue === '' ? 0 : Number(rawValue);
        const maxPoints = Math.min(points.balance, dealLimit);
        const clampedValue = isNaN(value) ? 0 : Math.min(Math.max(0, value), maxPoints);
        setSelectedPoints(clampedValue);
        setProgress(maxPoints > 0 ? (clampedValue / maxPoints) * 100 : 0);
        // Auto-update price when slider changes
        PointsActions.saveChosenUserPoints(clampedValue);
    };

    const handleSliderChange = (e) => {
        if (!points || dealLimit <= 0) return;
        const value = Number(e.target.value);
        const maxPoints = Math.min(points.balance, dealLimit);
        const clampedValue = Math.min(Math.max(0, value), maxPoints);
        setSelectedPoints(clampedValue);
        setProgress(maxPoints > 0 ? (clampedValue / maxPoints) * 100 : 0);
        // Auto-update price when slider changes
        PointsActions.saveChosenUserPoints(clampedValue);
    };

    // Pointer/key release marks the end of one slider gesture, not every intermediate value.
    const handleSliderRelease = () => {
        Analytics.actions.points.pointsSliderInteraction(profile);
    };

    const maxPoints = Math.min(points?.balance || 0, dealLimit);
    const isEmpty = !points || !points?.balance || points.balance === 0;
    const isDisabled = isEmpty || isFormLocked || maxPoints <= 0;

    return (
        <div className={classNames('points-slider', { disabled: isDisabled })} data-testid="points-slider">
            <div className="points-slider__header">
                <div className="points-slider__title">
                    {userName && points?.member_number
                        ? t('PointsSlider.RedeemPoints', { name: userName, id: points?.member_number })
                        : t('PointsSlider.Title')}
                </div>
            </div>
            <div className="points-slider__content">
                <div className="points-slider__top-row">
                    {isElalProvider && (
                        <div className="points-slider__branding">
                            <ElalMatmidLogo />
                        </div>
                    )}
                    {isEmpty ? (
                        <div className="points-slider__empty-state">
                            <p>{t('PointsSlider.NoPointsToRedeem')}</p>
                        </div>
                    ) : (
                        <div className="points-slider__use-input">
                            <div className="points-slider__points-label">{t('PointsSlider.Use')}</div>
                            <input
                                type="text"
                                min="0"
                                max={maxPoints}
                                value={selectedPoints.toLocaleString()}
                                onChange={handleChange}
                                className="points-slider__input"
                                data-testid="points-slider-value"
                                disabled={isDisabled}
                            />
                            <div className="points-slider__points-label">{t('Points')}</div>
                        </div>
                    )}
                </div>
                <div className="points-slider__controls-wrapper">
                    <div className={`points-slider__slider ${isDisabled ? 'disabled' : ''}`}>
                        <input
                            type="range"
                            min="0"
                            max={maxPoints}
                            value={isEmpty ? 0 : selectedPoints}
                            onChange={handleSliderChange}
                            onPointerUp={handleSliderRelease}
                            onKeyUp={(e) => SLIDER_VALUE_KEYS.includes(e.key) && handleSliderRelease()}
                            className="points-slider__range"
                            style={{ '--progress': `${progress}%` }}
                            disabled={isDisabled}
                        />
                    </div>
                    {!isEmpty && (
                        <div className="points-slider__info">
                            <span className="points-slider__balance">
                                {t('PointsSlider.YouHave', { points: points.balance.toLocaleString() })}
                            </span>
                            <span className="points-slider__limit">
                                {t('PointsSlider.DealLimit', { points: dealLimit.toLocaleString() })}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PointsSliderComponent;
